import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG = "https://images.unsplash.com/photo-1601929862217-f1bf94503333?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBkYXJrfGVufDB8fHx8MTc4NzY1NDIzNnww&ixlib=rb-4.1.0&q=85";

const PRICELIST_HOSTS = ["forzamobil.id", "www.forzamobil.id"];
const homePath = () => (PRICELIST_HOSTS.includes(window.location.hostname) ? "/dashboard" : "/");

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("remember_email") || "");
  const [remember, setRemember] = useState(() => !!localStorage.getItem("remember_email"));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to={homePath()} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      if (remember) localStorage.setItem("remember_email", email); else localStorage.removeItem("remember_email");
      navigate(homePath());
    } catch (err) {
      setError(apiError(err, "Gagal masuk"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm fade-up">
          <div className="flex items-center gap-3 mb-10">
            <img src="/logo.jpg" alt="ForzaMobil.id" className="w-12 h-12 rounded-full object-cover ring-1 ring-white/10" />
            <div>
              <p className="font-heading text-xl font-extrabold text-zinc-50 leading-none">ForzaMobil.id</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Used Cars Dealer</p>
            </div>
          </div>

          <h1 className="text-3xl font-extrabold tracking-tight text-zinc-50 mb-2">Masuk</h1>
          <p className="text-sm text-zinc-400 mb-8">Kelola stok, transaksi, dan akuntansi showroom Anda.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@email.com"
                className="h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Kata Sandi</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer select-none">
              <input
                data-testid="remember-me-checkbox"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 accent-blue-500"
              />
              Ingat saya (email tersimpan & browser menawarkan simpan kata sandi)
            </label>
            {error && (
              <p data-testid="login-error" className="text-sm text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2">
                {error}
              </p>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full h-10 bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold rounded-md transition-colors duration-200"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Masuk ke Dashboard"}
            </Button>
          </form>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img src={BG} alt="Showroom" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-500 mb-3">All-in-One ERP</p>
          <h2 className="font-heading text-4xl font-extrabold tracking-tight text-white max-w-md leading-tight">
            Pembelian, biaya, penjualan & akuntansi dalam satu dashboard.
          </h2>
        </div>
      </div>
    </div>
  );
}
